// publicSiteHandoff.js — "Continue with senTavus Desk" on sentavus.com (owner 2026-09-18: agents sign in to the
// public site from their ACTIVE Desk session, the way "Continue with Google" works — no second password).
//
// The flow, like an OAuth implicit grant between two sites we own:
//   1. sentavus.com sends the agent to  app.sentavus.com/public-login?state=<nonce>&return=<path>
//   2. here: if the Desk already has a session, hand it back at once; if not, the normal LoginScreen runs (with its
//      core gate — resigned/revoked agents stay out) and the hand-back happens on SIGNED_IN.
//   3. back to  https://sentavus.com/agent/callback#access_token=…&expires_at=…&state=…&return=…
//
// What leaves the Desk: the short-lived ACCESS token only (≈1 h), in the URL FRAGMENT (never sent to a server,
// never in a Referer). The REFRESH token never leaves — the Desk keeps sole ownership of its session, so nothing
// the public site does can rotate it out from under the Desk. The destination ORIGIN is fixed in this file; the
// request only chooses a PATH on it, so this page can never be used to send a token anywhere else. The nonce is
// checked by sentavus.com, so nobody can make a browser adopt someone else's token.
//
// A request waits at most PENDING_MS for the agent to log in; after that it is dropped rather than firing later.

export const PUBLIC_SITE_ORIGIN = 'https://sentavus.com'
export const HANDOFF_PATH = '/public-login'
const STORE_KEY = 'sva-public-handoff'
const PENDING_MS = 10 * 60 * 1000
// ⚠️ A hand-back is a navigation, and a LATER navigation cancels it: App.jsx reloads the page when a lazy chunk fails
// to load (right after a Desk deploy — and the app only starts loading its chunks once the agent is signed in), and
// main.jsx has its boot retry. The request used to be dropped just before leaving, so after such a reload the agent
// simply stayed in the Desk (owner 2026-10-14: "I log in desk and then it doesn't log me in sentavus, I stay in desk").
// It is now kept for ONE more try inside this window, then dropped — short, so an ordinary Desk visit in the same tab
// a moment later is not bounced to the site. The site's nonce is single-use, so a second hand-back signs nobody in twice.
const RETRY_MS = 10 * 1000
const STATE_RE = /^[A-Za-z0-9_-]{16,128}$/

// a same-site path, nothing else: no scheme, no host, no protocol-relative //host, no backslash tricks
export function safeReturnPath(p) {
  if (typeof p !== 'string' || p.length > 512) return '/'
  if (!p.startsWith('/') || p.startsWith('//') || p.includes('\\') || /[\x00-\x1f]/.test(p)) return '/'
  return p
}

// parse the request this page was opened with; null when it is not a valid hand-off request
export function parseRequest(search) {
  const q = new URLSearchParams(search || '')
  const state = q.get('state') || ''
  if (!STATE_RE.test(state)) return null
  return { state, ret: safeReturnPath(q.get('return') || '/') }
}

// where to send the agent back to, carrying the token in the fragment
export function callbackUrl({ state, ret }, session) {
  const f = new URLSearchParams({
    access_token: session.access_token,
    expires_at: String(session.expires_at || ''),
    state,
    return: safeReturnPath(ret),
  })
  return `${PUBLIC_SITE_ORIGIN}/agent/callback#${f.toString()}`
}

function save(req) {
  try { sessionStorage.setItem(STORE_KEY, JSON.stringify({ ...req, at: Date.now() })) } catch { /* private mode */ }
}
// is a stored request still owed a hand-back? Waiting for the login: PENDING_MS. Already handed back once: only
// inside RETRY_MS (the navigation may have been cancelled by a reload).
export function stillPending(req, now = Date.now()) {
  if (!req || !STATE_RE.test(req.state || '') || !(now - (req.at || 0) < PENDING_MS)) return false
  return req.handedAt == null || now - req.handedAt < RETRY_MS
}
function takePending() {
  try {
    const raw = sessionStorage.getItem(STORE_KEY)
    if (!raw) return null
    const req = JSON.parse(raw)
    if (!stillPending(req)) {
      sessionStorage.removeItem(STORE_KEY)
      return null
    }
    return req
  } catch { return null }
}
function clear() { try { sessionStorage.removeItem(STORE_KEY) } catch { /* private mode */ } }

function handBack(req, session) {
  // the first hand-back keeps the request for one retry; the retry drops it (never a third)
  if (req.handedAt == null) { try { sessionStorage.setItem(STORE_KEY, JSON.stringify({ ...req, handedAt: Date.now() })) } catch { /* private mode */ } }
  else clear()
  window.location.replace(callbackUrl(req, session))
}

// Called once from main.jsx, before the app renders. Returns true when this page load IS the hand-off and the app
// must not render (we are leaving); false to render the Desk normally (e.g. to show the login screen).
export async function runPublicSiteHandoff(supabase) {
  if (!supabase) return false
  const onHandoffPath = window.location.pathname === HANDOFF_PATH
  if (onHandoffPath) {
    const req = parseRequest(window.location.search)
    // a malformed request: drop it and open the Desk as usual
    if (!req) { window.history.replaceState(null, '', '/'); return false }
    const { data } = await supabase.auth.getSession()   // the Desk's own client — refreshes if it must
    if (data?.session?.access_token) { handBack(req, data.session); return true }
    // not signed in: remember the request, show the normal login at '/', hand back on SIGNED_IN
    save(req)
    window.history.replaceState(null, '', '/')
  }
  const pending = takePending()
  if (pending) {
    // a page load that already HAS a session (a reload after the login, a session restored on load): hand back now.
    // Such a session arrives as INITIAL_SESSION, never SIGNED_IN, so waiting for the event waited for ever.
    if (!onHandoffPath) {
      const { data: cur } = await supabase.auth.getSession()
      if (cur?.session?.access_token) { handBack(pending, cur.session); return true }
    }
    // ANY event that carries a session while a request waits — not only SIGNED_IN: setSession (LoginScreen) reports a
    // token the device clock reads as expired as TOKEN_REFRESHED, and a restored one as INITIAL_SESSION.
    const { data } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!session?.access_token) return
      const req = takePending()
      if (!req) return
      data.subscription.unsubscribe()
      handBack(req, session)
    })
  }
  return false
}
